import React, { useState } from 'react'
import { Modal } from 'oio-react'
import { Route, Switch, useHistory, useParams, useRouteMatch } from 'react-router-dom'
import BlogSettings from 'src/apps/Blog/apps/Settings'
import BlogView from 'src/apps/Blog/apps/View'
import InitiativeCheckInDesk from 'src/apps/CheckInDesk'
import DiscussionView from 'src/apps/Discussions/apps/View'
import DiscussionSettings from 'src/apps/Discussions/apps/Settings'
import ResourceView from 'src/apps/Resource/apps/View'
import ResourceSettings from 'src/apps/Resource/apps/Settings'
import ContainerLayout1 from 'src/components/Initiative/ContainerLayout1'
import InitiativeJoin from 'src/components/Initiative/Join'
import HomeLayout1 from 'src/components/Initiative/HomeLayout1'
import { InitiativeHierarchyProvider } from 'src/libraries/contexts/InitiativeHierarchy'
import { useInitiative } from 'src/graphql/hooks'
// TODO: All the below will be moved to the apps/Initiative directory
// See PR #285
import InitiativeFiles from 'src/pages/Initiative/Files'
import InitiativePage from 'src/pages/Initiative/Page'
import InitiativePeople from 'src/pages/Initiative/People'
import InitiativeSchedule from 'src/pages/Initiative/Schedule'
import InitiativeSettings from 'src/pages/Initiative/Settings'
import InitiativeActions from '~/Initiative/components/Actions'

// =======================================================
// Layouts
// =======================================================

const containerLayouts = [null, ContainerLayout1]
const homeLayouts = [null, HomeLayout1]

// =======================================================
// Initiative
// =======================================================

const Initiative = () => {
   const history = useHistory()
   const match = useRouteMatch()
   const { slug } = useParams()
   const initiativeFilter = {}

   // TODO: DEPRECATE WHEN WE COMBINE SLUGS AND URLS
   // See PR #285
   if (match.url.startsWith('/initiatives')) {
      initiativeFilter.slug = slug
   } else {
      initiativeFilter.url = match.url.substring(1)
   }

   const [modalIsOpen, setModalIsOpen] = useState(true)
   const { initiative, networkStatus, refetch } = useInitiative(initiativeFilter)

   // We don't want to display our "loading" state if the initiative
   // list is being refetched
   const initialLoading = networkStatus <= 2
   if (initialLoading) {
      return null
   }

   const { currentUserCanEdit } = initiative
   const isUrlOrFilesResource = initiative.class === 'resource' &&
      ['url', 'files'].includes(initiative.resourceFormat)

   // TODO: Rename this to something institutionalized
   // See PR #427
   const isBasicInitiative = ['discussion', 'post'].includes(initiative.class) ||
      isUrlOrFilesResource

   const containerLayoutKey = initiative.containerLayoutKey || 1
   let InitiativeContainerLayout = containerLayouts[containerLayoutKey] || ContainerLayout1

   if (initiative.class === 'discussion') {
      InitiativeContainerLayout = DiscussionView
   } else if (initiative.class === 'post') {
      InitiativeContainerLayout = BlogView
   } else if (isUrlOrFilesResource) {
      InitiativeContainerLayout = ResourceView
   }

   const InitiativeHomeLayout = !isBasicInitiative
      ? homeLayouts[1]
      : () => null

   return (
      <InitiativeHierarchyProvider
         initiative={initiative}
         refetchInitiative={refetch}>
         <InitiativeContainerLayout>
            <Switch>
               <Route
                  exact
                  path={match.path}
                  component={InitiativeHomeLayout}
               />
               <Route path={`${match.path}/join`}>
                  <InitiativeJoin onClose={() => history.push(match.url)} />
                  <InitiativeHomeLayout />
               </Route>
               <Route
                  path={`${match.path}/files`}
                  component={InitiativeFiles}
               />
               <Route
                  path={`${match.path}/people`}
                  component={InitiativePeople}
               />
               {initiative.class === 'event' && (
                  <Route
                     path={`${match.path}/schedule/:selectedDateKey?`}
                     component={InitiativeSchedule}
                  />
               )}
               <Route
                  path={`${match.path}/pages/:pageSlug`}
                  component={InitiativePage}
               />
               {currentUserCanEdit && (
                  <Route path={`${match.path}/checkin`}>
                     <Modal
                        borderRadius="6px"
                        overlayBackgroundColor="rgba(0,0,0,0.85)"
                        width="100%"
                        height="100%"
                        onCloseComplete={() => {
                           history.push(match.url)
                           setModalIsOpen(true)
                        }}
                        onCloseTrigger={() => setModalIsOpen(false)}
                        open={modalIsOpen}>
                        <InitiativeCheckInDesk returnUrl={match.url} />
                     </Modal>
                  </Route>
               )}
               {currentUserCanEdit && (
                  <Route path={`${match.path}/settings`}>
                     <InitiativeHomeLayout />
                     <Modal
                        borderRadius="3px"
                        overlayBackgroundColor="rgba(45,40,40,0.9)"
                        height={isBasicInitiative
                           ? '100%'
                           : '100%[a-b] 85%[c-f]'
                        }
                        width={isBasicInitiative
                           ? '100%[a-b] 90%[c-d] 100%[e-f]'
                           : '100%[a-b] 90%[c-d] 1200px[e-f]'
                        }
                        maxWidth={isBasicInitiative && '1000px'}
                        onCloseComplete={() => {
                           history.push(match.url)
                           setModalIsOpen(true)
                        }}
                        onCloseTrigger={() => {
                           // TODO: Improve this
                           // If initiative is a discussion or post, do not allow
                           // user to close modal by clicking overlay
                           // This is to prevent accidentally closing when user is actually
                           // editing their discussion. In the future, we will probably do a
                           // check if the editor is "dirty"
                           // See PR #419, Issue #424
                           if (isBasicInitiative) {
                              setModalIsOpen(false)
                           }
                        }}
                        open={modalIsOpen}>
                        {initiative.class === 'discussion' && (
                           <DiscussionSettings returnUrl={match.url} />
                        )}
                        {initiative.class === 'post' && (
                           <BlogSettings returnUrl={match.url} />
                        )}
                        {isUrlOrFilesResource && (
                           <ResourceSettings returnUrl={match.url} />
                        )}
                        {!isBasicInitiative && (
                           <InitiativeSettings returnUrl={match.url} />
                        )}
                     </Modal>
                  </Route>
               )}
            </Switch>
         </InitiativeContainerLayout>
         <InitiativeActions />
      </InitiativeHierarchyProvider>
   )
}

export default Initiative
